import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MaterialFlowChart from "../../../components/chart/MaterialFlowChart";

// Input sources: everything except virgin feedstock counts as circular.
const INPUT_SERIES = [
  { key: "im_recycled_content", label: "Recycled content", legendLabel: "Recycled", flow: "recycled", circular: true },
  { key: "im_reuse", label: "Reuse", flow: "reuse", circular: true },
  { key: "im_remanufactured", label: "Remanufactured", flow: "remanufactured", circular: true },
  { key: "im_bio_materials", label: "Biological materials", legendLabel: "Biological / composted", flow: "biological", circular: true },
  { key: "im_virgin_content", label: "Virgin content", legendLabel: "Virgin", flow: "virgin", circular: false },
];

// End-of-life destinations: everything except waste to landfill counts as circular.
const OUTPUT_SERIES = [
  { key: "om_recycling", label: "Recycled", flow: "recycled", circular: true },
  { key: "om_reuse", label: "Reuse", flow: "reuse", circular: true },
  { key: "om_bio_composted", label: "Composted", flow: "biological", circular: true },
  { key: "om_bio_energy_recovery", label: "Energy recovery", flow: "energyRecovery", circular: true },
  { key: "om_waste_content", label: "Waste / landfill", flow: "waste", circular: false },
];

const toPercent = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? Math.max(0, num) : 0;
};

/**
 * Builds one side of a flow row; the non-circular remainder (virgin / waste) is
 * always derived as 100 minus the circular shares so each bar totals 100%.
 */
const buildSide = (item, series) => {
  const values = {};
  let circularSum = 0;
  series.forEach((s) => {
    if (s.circular) {
      values[s.key] = toPercent(item[s.key]);
      circularSum += values[s.key];
    }
  });
  const remainder = series.find((s) => !s.circular);
  if (remainder) values[remainder.key] = Math.max(0, 100 - circularSum);
  return values;
};

const CircularityFlowChart = ({ circularities = [], title = "Material Flows" }) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () =>
      (circularities || []).map((item, idx) => ({
        id: item.env_id ?? item.id ?? idx,
        label: item.description || item.name || t("Unknown Material"),
        input: buildSide(item, INPUT_SERIES),
        output: buildSide(item, OUTPUT_SERIES),
      })),
    [circularities, t]
  );

  return <MaterialFlowChart title={title} rows={rows} inputSeries={INPUT_SERIES} outputSeries={OUTPUT_SERIES} />;
};

export default CircularityFlowChart;
